import { Box, Accordion, AccordionSummary, Stack, Typography, AccordionDetails, Card, Chip, Divider, TextField, InputAdornment, Paper, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { Answers, AnswersGroup, Student, TaskChatItem, TaskDefinition } from "../Types";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SearchIcon from "@mui/icons-material/Search";
import { PieChart } from "@mui/x-charts/PieChart";
import { Timestamp } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { indigo } from "@mui/material/colors";

interface Props {
  taskDef: TaskDefinition;
  taskIndex?: number;
  students: Student[];
  isLive: boolean;
  isSolo: boolean;
  reached?: boolean;
  discussing?: boolean;
  answeringFinal?: boolean;
  taskFinished?: boolean;
  answers: Answers<AnswersGroup>;
  answersInitial?: Answers<AnswersGroup>;
  answerFinal: AnswersGroup[];
  chat?: TaskChatItem[];
  timerMax: number;
  timerStarted?: Timestamp;
}

interface ChartData {
  id: number;
  value: number;
  label: string;
}

export default function TaskAccordion(props: Props) {
  const [timerCurrent, setTimerCurrent] = useState(0);
  const timerMaxRef = useRef(props.timerMax);
  const timerStartTimeSecRef = useRef(props.timerStarted?.seconds ?? 0);
  const [chatSearch, setChatSearch] = useState<string>("");
  const [isShowingInitialAnswers, setIsShowingInitialAnswers] = useState<boolean>(true);

  useEffect(() => {
    timerMaxRef.current = props.timerMax;
    timerStartTimeSecRef.current = props.timerStarted?.seconds ?? 0;
  }, [props.timerMax, props.timerStarted]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (timerMaxRef.current === 0 || timerMaxRef.current === undefined) {
        setTimerCurrent(0);
      } else {
        let timeNow: Date = new Date(Date.now());
        let timeStartedMs: number = timerStartTimeSecRef.current * 1000;
        let secondsPast = (timeNow.getTime() - timeStartedMs) / 1000;
        setTimerCurrent(Math.floor(timerMaxRef.current - secondsPast));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  function NumArraysEqual(a: number[], b: number[]): boolean {
    if (a === undefined || b === undefined) return false;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      if (!b.includes(a[i])) return false;
    }

    return true;
  }

  function GetAnsweredCorrectlyCount(correctAnswers: AnswersGroup[], answers: Answers<AnswersGroup>): number {
    const keys: string[] = Object.keys(answers);
    let correctCount: number = 0;
    let isCorrect: boolean = true;

    for (let k = 0; k < keys.length; k++) {
      isCorrect = true;
      for (let i = 0; i < answers[keys[k]].length; i++) {
        if (!NumArraysEqual(answers[keys[k]][i]?.indexes, correctAnswers[i]?.indexes)) {
          isCorrect = false;
          break;
        }
      }
      if (isCorrect) correctCount++;
    }

    return correctCount;
  }

  function AreAnswersMajorityWrong(answersCorrect: AnswersGroup[]): boolean {
    if (props.answers === undefined) return false;

    const answers: Answers<AnswersGroup> = props.answers;
    let correctCount: number = 0;
    let isCorrect: boolean = true;
    const keys: string[] = Object.keys(answers);
    if (keys.length === 0) return false;

    for (let k = 0; k < keys.length; k++) {
      isCorrect = true;
      for (let i = 0; i < answers[keys[k]].length; i++) {
        if (!NumArraysEqual(answers[keys[k]][i].indexes, answersCorrect[i].indexes)) {
          isCorrect = false;
          break;
        }
      }
      if (isCorrect) correctCount++;
    }

    return correctCount > keys.length / 2 ? false : true;
  }

  // App was crashing when clicking on a Chip node without
  // a function assigned to onClick
  function hold() { }

  function TaskStatusChip() {
    if (props.taskFinished) {
      return <Chip onClick={hold} color="success" label="Answered" />;
    } else if (props.answeringFinal) {
      return <Chip onClick={hold} color="info" label="Submitting Final Answers" />;
    } else if (props.discussing) {
      return <Chip onClick={hold} color="info" label="Discussing" />;
    } else if (props.reached) {
      return <Chip onClick={hold} color="info" label="Submitting Initial Answers" />;
    } else {
      return <Chip onClick={hold} label="Not Reached" />;
    }
  }

  function AnswersGroupsDisplay(answerGroups: AnswersGroup[]) {
    return (
      <Stack sx={{ flexGrow: 1, justifyContent: "center", alignItems: "center" }}>
        {IsAnswersGroupEmpty(answerGroups[0]) ? (
          <Typography>No Majority</Typography>
        ) : (
          answerGroups.map((group, i) => {
            return (
              <Typography textAlign={"left"} variant="h6" key={i}>
                {answerGroups.length > 1 ? `${group.title}: ` : ""}
                {group.indexes.map((index: number) => {
                  return String.fromCharCode(index + 65).toUpperCase();
                })}
              </Typography>
            );
          })
        )}
      </Stack>
    );
  }

  function IsAnswersGroupEmpty(value: AnswersGroup): boolean {
    if (value === null || value === undefined) return true;
    if (value.indexes.length === 0) return true;
    if (value.indexes[0] === -1) return true;
    return false;
  }

  function IndexesToLetters(indexes: number[]): string {
    const sorted: number[] = indexes.sort();
    let value: string = "";

    sorted.forEach((i) => {
      if (i >= 0) {
        value += String.fromCharCode(i + 65);
      } else {
        value += "_";
      }
    });

    return value;
  }

  function TaskAnswerCharts(answers: Answers<AnswersGroup>) {
    var voteMapArray: Map<string, number>[] = [];
    var chartDataArray: ChartData[][] = [];
    const keys: string[] = Object.keys(answers);

    for (var i = 0; i < answers[keys[0]]?.length; i++) {
      if (i >= voteMapArray.length) {
        voteMapArray.push(new Map());
      }
    }

    keys.forEach((key: string) => {
      for (var i = 0; i < answers[key].length; i++) {
        let letters: string = IndexesToLetters(answers[key][i].indexes);
        if (voteMapArray[i].has(letters)) {
          let votes = voteMapArray[i].get(letters);
          let count = votes !== undefined ? votes + 1 : 1;
          voteMapArray[i].set(letters, count);
        } else {
          voteMapArray[i].set(letters, 1);
        }
      }
    });

    voteMapArray.forEach((voteMap, index) => {
      chartDataArray.push([]);
      let counter: number = 0;
      voteMap.forEach((value, key) => {
        let percent = (value / keys.length) * 100;
        chartDataArray[index].push({
          id: counter,
          value: (value / keys.length) * 100,
          label: `${key} (${percent.toString()}%)`,
        });
        counter++;
      });
    });

    return (
      <>
        {chartDataArray.map((chartData: ChartData[], index) => {
          return <PieChart key={index} series={[{ data: chartData }]} width={100} height={100} />;
        })}
      </>
    );
  }

  function GetPlayerNameFromId(id: string) {
    for (let i = 0; i < props.students.length; i++) {
      if (props.students[i].id === id) return props.students[i].name;
    }

    return "player";
  }

  function HandleChatSearch(event: any) {
    setChatSearch(event.target.value);
  }

  function TaskChatFiltered(chat: TaskChatItem[] | undefined, chatSearch: string) {
    return chat?.filter((item: TaskChatItem) => {
      let search: string = chatSearch.toLowerCase();
      let name: string = GetPlayerNameFromId(item.playerId).toLowerCase();
      let msg: string = item.message.toLowerCase();

      return name.includes(search) || msg.includes(search);
    });
  }

  function TimestampToDateHourMin(ts: Timestamp) {
    var date: Date = ts.toDate();
    var postfix: string = date.getHours() >= 12 ? "pm" : "am";
    return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")} ${postfix}`;
  }

  function AnswersGroupsToText(answers: AnswersGroup[]): string {
    var value: string = "";

    answers.forEach((answerGroup: AnswersGroup) => {
      if (answers.length > 1) value += `${answerGroup.title}: `;
      value += `${IndexesToLetters(answerGroup.indexes)} `;
    });

    return value;
  }

  function TaskAnswerDisplay(index: number) {
    if (!props.reached || props.answers === undefined || props.answers === null) {
      return (
        <Box
          sx={{
            minHeight: 155,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Task Not Reached
        </Box>
      );
    } else {
      let currentAnswers: Answers<AnswersGroup>;
      if (!props.isSolo && isShowingInitialAnswers && props.answersInitial !== undefined) {
        currentAnswers = props.answersInitial;
      } else {
        currentAnswers = props.answers;
      }
      let answeredCorrectlyCount: number = GetAnsweredCorrectlyCount(props.taskDef.correctAnswersGroups, currentAnswers);
      let answersKeys: string[] = Object.keys(currentAnswers);
      let answeredCount: number = answersKeys.length;
      let percentCorrect: number = Math.round((answeredCorrectlyCount / answeredCount) * 100);
      return (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {props.isSolo ? (
            <></>
          ) : (
            <ToggleButtonGroup value={isShowingInitialAnswers} exclusive onChange={HandleIsShowingInitialAnswersChange} aria-label="answer display" sx={{ boxShadow: "none" }}>
              <ToggleButton value={true} aria-label="left aligned">
                Initial Answers
              </ToggleButton>
              <ToggleButton value={false} aria-label="left aligned">
                Final Answers
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Stack direction="row" sx={{ height: "200px", alignItems: "center" }}>
            {/* <Stack sx={{ height: "100%", alignItems: 'center', flexGrow: 1 }}>
							<Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
								<Typography variant="caption">Correct Answer(s)</Typography>
							</Box>
							{AnswersGroupsDisplay(props.taskDef.correctAnswersGroups)}
						</Stack>
						<Divider orientation="vertical" flexItem></Divider> */}
            <Stack sx={{ height: "100%", alignItems: "center", flexGrow: 1 }}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" textAlign={"left"}>
                  Voting
                </Typography>
              </Box>
              <Stack spacing={1} sx={{ height: "100%" }}>
                {currentAnswers === null || currentAnswers === undefined || Object.keys(currentAnswers).length <= 0 ? (
                  <Stack
                    sx={{
                      flexGrow: 1,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typography textAlign={"left"}>Question not answered</Typography>
                  </Stack>
                ) : (
                  <>
                    <Stack direction={"row"} sx={{ flexGrow: 1 }}>
                      {TaskAnswerCharts(currentAnswers)}
                    </Stack>
                    <Typography sx={{ color: "gray" }}>
                      {answeredCorrectlyCount}/{answeredCount} ({percentCorrect}
                      %) answered correctly
                    </Typography>
                  </>
                )}
              </Stack>
            </Stack>
            {props.isSolo ? (
              <></>
            ) : (
              <>
                {isShowingInitialAnswers ? (
                  <></>
                ) : (
                  <Stack sx={{ height: "100%", alignItems: "center", flexGrow: 1 }}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="caption">Final Answer</Typography>
                    </Box>
                    {AnswersGroupsDisplay(props.answerFinal)}
                  </Stack>
                )}
                <Divider orientation="vertical" flexItem></Divider>
                <Stack
                  spacing={1}
                  sx={{
                    height: "100%",
                    alignItems: "center",
                    flexGrow: 1,
                    px: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="caption">Chat</Typography>
                  </Box>
                  <TextField
                    sx={{ width: "100%" }}
                    onChange={(event) => {
                      HandleChatSearch(event);
                    }}
                    placeholder="Search"
                    variant="outlined"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <Card
                    variant="outlined"
                    sx={{
                      width: "100%",
                      height: "100%",
                      boxSizing: "border-box",
                      p: 1,
                      overflowY: "scroll",
                    }}
                  >
                    {TaskChatFiltered(props.chat, chatSearch)?.map((value, index) => {
                      return (
                        <Paper key={index} variant="outlined" sx={{ p: 1, mb: 1 }}>
                          <Typography variant="subtitle2" textAlign={"left"}>
                            {GetPlayerNameFromId(value.playerId)}
                          </Typography>
                          <Typography variant="body2" textAlign={"left"} sx={{ my: 1 }}>
                            {value.message}
                          </Typography>
                          <Typography
                            textAlign={"right"}
                            variant="body2"
                            sx={{
                              width: "100%",
                              fontSize: "0.75rem",
                              color: "gray",
                            }}
                          >
                            {TimestampToDateHourMin(value.timeStamp)}
                          </Typography>
                        </Paper>
                      );
                    })}
                  </Card>
                </Stack>
              </>
            )}
          </Stack>
          <Box>
            <Accordion>
              <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                <Typography component="span">Submitted Answers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {answersKeys.map((answerKey, index) => {
                  return (
                    <Typography key={index} textAlign={"left"}>
                      {GetPlayerNameFromId(answerKey)} - {AnswersGroupsToText(currentAnswers[answerKey])}
                    </Typography>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Stack>
      );
    }
  }

  function HandleIsShowingInitialAnswersChange(event: React.MouseEvent<HTMLElement>, newValue: boolean) {
    setIsShowingInitialAnswers(newValue);
  }

  return (
    <Box key={props.taskDef.id}>
      <Accordion>
        <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
          <Stack direction="row" sx={{ width: 1, justifyContent: "space-between", mr: 1 }}>
            <Stack direction="row" spacing={1} sx={props.isLive ? { alignItems: "center" } : { width: 1, justifyContent: "space-between", mr: 1 }}>
              <Typography component="span">{props.taskDef.name}</Typography>
              {AreAnswersMajorityWrong(props.taskDef.correctAnswersGroups) ? (
                <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ color: "#ef5350" }}>{" < 60%"}</Typography>
                  <WarningAmberRoundedIcon sx={{ color: "#ef5350" }}></WarningAmberRoundedIcon>
                </Stack>
              ) : (
                <></>
              )}
            </Stack>
            {props.isLive ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography>{timerCurrent === 0 ? "" : timerCurrent}</Typography>
                {TaskStatusChip()}
              </Stack>
            ) : (
              <></>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Card>
            <Typography textAlign="left">{props.taskDef.question}</Typography>
            <Box sx={{ mt: 1 }}>
              <Box
                sx={{
                  pb: 1,
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    backgroundColor: indigo[50],
                    color: indigo[900],
                    border: `1px solid var(--template-palette-divider)`,
                    borderRadius: 0.5,
                    py: 0.5,
                    px: 1,
                  }}
                >
                  {props.taskDef.correctAnswersGroups.length === 1 ? "Multiple Choice" : "Drag and Drop"}
                </Box>
                <Typography>Correct Answer(s): {AnswersGroupsToText(props.taskDef.correctAnswersGroups)}</Typography>
              </Box>
              <Box>
                {props.taskDef.answers.map((answer, index) => {
                  return (
                    <Accordion key={index}>
                      <AccordionSummary
                        sx={{
                          pl: 1,
                          py: 0.5,
                          minHeight: 0,
                          fontSize: "0.875rem",
                        }}
                        expandIcon={<ArrowDropDownIcon />}
                      >
                        {"(" + String.fromCharCode(index + 65) + ")"} {answer.text}
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography sx={{ fontSize: "0.875rem", textAlign: "left" }}>{answer.feedback}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </Box>
          </Card>
          {TaskAnswerDisplay(props.taskDef.taskIndex)}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
